import { buildUtagUrl, TEALIUM_CDN_ORIGIN, type TealiumRuntimeConfig } from '@/lib/tealium';

type TagManagerProps = {
    tealium: TealiumRuntimeConfig | null;
    nonce?: string;
};

/**
 * Serialize a value for embedding in an inline script. Escapes the characters that
 * would otherwise let a preference value break out of the `<script>` element or
 * terminate the statement.
 */
function serializeForScript(value: unknown): string {
    return JSON.stringify(value)
        .replaceAll('<', String.raw`\u003c`)
        .replaceAll('>', String.raw`\u003e`)
        .replaceAll('&', String.raw`\u0026`)
        .replaceAll('\u{2028}', String.raw`\u2028`)
        .replaceAll('\u{2029}', String.raw`\u2029`);
}

/**
 * Tealium configuration, base data layer and `utag.sync.js`, rendered in the document `<head>`.
 *
 * `window.utag_data` must exist before `utag.js` runs, and `utag.sync.js` must load
 * synchronously — hence no `async` / `defer` on it.
 */
export function TagManagerHead({ tealium, nonce }: TagManagerProps) {
    if (!tealium) return null;

    const { settings, baseData } = tealium;
    const tealiumConfig = {
        account: settings.account,
        profile: settings.profile,
        env: settings.environment,
        baseUrl: `${TEALIUM_CDN_ORIGIN}/utag`,
    };

    return (
        <>
            <script
                nonce={nonce}
                // Browsers strip the `nonce` content attribute from the DOM after applying
                // CSP, which surfaces as a hydration attribute mismatch on hand-written
                // inline scripts. Same treatment as the __APP_CONFIG__ script.
                suppressHydrationWarning
                // eslint-disable-next-line react/no-danger -- controlled inline script built from validated site preferences, escaped by serializeForScript
                dangerouslySetInnerHTML={{
                    __html: `window.tealiumConfig = Object.assign(${serializeForScript(tealiumConfig)}, { initialPath: location.pathname });
window.utag_data = Object.assign(window.utag_data || {}, ${serializeForScript(baseData)});`,
                }}
            />
            <script src={buildUtagUrl(settings, 'utag.sync.js')} nonce={nonce} suppressHydrationWarning />
        </>
    );
}

/**
 * Asynchronous `utag.js` loader, rendered as the first element of the document `<body>`.
 *
 * The script element is created imperatively rather than rendered as `<script async src>`
 * because React hoists async scripts into the `<head>`, which would move the loader away
 * from the top of the body where Tealium expects it. For the same reason the new element is
 * anchored on `document.currentScript` rather than on the document's first `<script>` — the
 * latter lives in the `<head>`, and Tealium's stock snippet would land `utag.js` there.
 *
 * This markup is only ever emitted on the initial document, so SPA route changes never
 * reload `utag.js` — virtual page views are the SPA tracking layer's responsibility.
 */
export function TagManagerBody({ tealium, nonce }: TagManagerProps) {
    if (!tealium) return null;

    const utagUrl = serializeForScript(buildUtagUrl(tealium.settings, 'utag.js'));

    return (
        <script
            nonce={nonce}
            suppressHydrationWarning
            // eslint-disable-next-line react/no-danger -- controlled inline loader, the only URL interpolated is escaped by serializeForScript
            dangerouslySetInnerHTML={{
                __html: `(function(){var s=document.createElement('script');s.src=${utagUrl};s.type='text/javascript';s.async=true;var c=document.currentScript;if(c&&c.parentNode){c.parentNode.insertBefore(s,c.nextSibling);}else{document.body.appendChild(s);}})();`,
            }}
        />
    );
}
